// BEVÉTEL-ELŐREJELZÉS — a Pénzügy lap „Bevétel-előrejelzés" füle (READ-ONLY belső vezetői nézet). A tartalmat
// a legacy CRM RevenueForecast 1:1 portja adja: MRR/PRR/NRR/ARR/AR kártyák + „következő havi" kiemelt csík +
// 4 chart (MRR-trend / szolgáltatásonként / top10 ügyfél / főkategória-donut) + szűrő-sáv (hónap-léptető,
// státusz-multi, fő-/alkategória kaszkád, szolgáltatás/csomag/ügyfél/fiók). A BFF `/revenue-forecast`
// végpontjáról fogyaszt (minden pénz Decimal-STRING, deviza HUF).
//
// A NÉZET NEM ROUTE: a `useFinanceView` szerződés szerint `{ toolbar, content }`-et ad vissza, és a
// `routes/finance.tsx` fül-héja rakja a helyére. Így a lap három füle EGY `AppShell`-ben él, a saját
// eszköztárával — a hónap-állapot pedig a héjban lakik, tehát a fül-váltás nem rántja vissza a hónapot.
//
// Page-gate: `finance.revenue.view` (a héj ellenőrzi). A szolgáltatás/csomag/ügyfél/fiók szűrő-OPCIÓK a
// `serviceAssignmentRefs` végpontról jönnek, ami `service_assignments.manage` jogot igényel — ezért ezek a
// szűrők (a legacy-vel és a /service-assignments oldallal konzisztensen) csak manage-jog mellett jelennek
// meg; a hónap/státusz/kategória szűrő view-joggal is működik.
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BoltIcon,
  ChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { revenueForecastKeys } from '../api';
import {
  EyeButton,
  RevenueDetailDialog,
  ServiceDrilldown,
  scrollToServiceGroup,
  StatusBreakdownBadges,
  type DetailRow,
} from './details';
import { ServiceDetailsExport } from './service-details-export';
import { ChangeBadge, PlanVsActualValue } from './stat-card';

import {
  BarList,
  ChartCard,
  DonutChart,
  PlanVsActualChart,
  StackedBarList,
  TrendChart,
} from '@/components/shared/charts';
import { EmptyState } from '@/components/shared/empty-state';
import { HeroStatCard } from '@/components/shared/hero-stat-card';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { PageBody } from '@/components/shared/page-body';
import { type PageNotice } from '@/components/shared/page-notices';
import { StatCard } from '@/components/shared/stat-card';
import { badgeVisual } from '@/components/shared/domain-badge';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import type { FinanceView, FinanceViewProps } from '@/features/finance/view';
import { DataHorizonNotice } from '@/features/revenue-forecast/components/data-horizon-notice';
import { StatusCounts } from '@/features/revenue-forecast/components/status-counts';
import {
  isNonDefaultStatus,
  useRevenueFilters,
} from '@/features/revenue-forecast/use-revenue-filters';
import { api } from '@/lib/api';
import { isForbiddenStatus } from '@/lib/error-status';
import { formatMoney, formatMoneyShort } from '@/lib/format-money';
import { currentMonth, formatMonthAxisLabel, formatMonthLabel } from '@/lib/month';
import { usePermissions } from '@/lib/use-permissions';

/** A PRR frekvencia-bontás sorrendje a badge-eken (a kimutatás használja). */
const PERIODIC_FREQUENCIES = ['quarterly', 'semi_annual', 'annual'] as const;

/** A kimutatás-nézet + a szűkítés ténye (az Összegzés fül összkép-kártyáinak kell — lásd `use-summary-view`). */
export interface ForecastView extends FinanceView {
  /** Van-e AKTÍV szűkítés (kereső / státusz / kategória / szolgáltatás / ügyfél) az alap-nézethez képest. */
  hasNarrowing: boolean;
}

export function useForecastView({ enabled, month, onMonthChange }: FinanceViewProps): ForecastView {
  const { t, i18n } = useTranslation();
  // A KIHAGYÁS-NAPLÓ mélylinkje külön jogot kíván (`invoices.view`) — a lap saját kapuja `finance.revenue.view`.
  const { can } = usePermissions();
  const locale = i18n.language;
  const fmt = (v: string | null | undefined): string => formatMoney(v, 'HUF', locale);
  const fmtShort = (v: string | null | undefined): string => formatMoneyShort(v, 'HUF', locale);

  // A SZŰRŐK a KÖZÖS hookból (`use-revenue-filters.ts`): ugyanaz a készlet, ugyanazok az URL-kulcsok,
  // mint a Bevételek fülön — a fül-váltás nem veszíti el a szűrést, és a két nézet ugyanazt a halmazt látja.
  const {
    filterItems,
    searchInput,
    setSearchInput,
    filters,
    query,
    scoped,
    statuses,
    billingType,
    hasNarrowing,
    selectService,
    selectStatus,
  } = useRevenueFilters({ enabled, month });
  // „ESETI" díjtípus-nézet: az ismétlődő-only blokkok (MRR-trend, státusz-darabszámok) értelmetlenek — a
  // BFF ezekre nullát / üres halmazt ad, a felület pedig elrejti őket, és egy megjegyzés mondja ki, miért
  // 0 az MRR / PRR / ARR (user-döntés: ne hibának látsszon)
  const oneTimeOnly = billingType === 'one_time';

  const forecastKey = revenueForecastKeys.forecast(filters);
  // A LEKÉRÉS DRÁGA: a BFF kilenc teljes-listás Odin-olvasásból építi a képet (hozzárendelések · árak ·
  // ügyfelek · fiókok · katalógus · usage · pivot), tehát EGY hónap-léptetés több tucat busz-kérést indít
  // (mérve: **30** — az 500-as lapméret előtt 104 volt). A hónapok közti oda-vissza lépkedés
  // emiatt kimerítette az Odin kérés-plafonját (600/perc), és a fojtás hibás lapot adott. Az ÖT PERCES
  // frissesség ezt zárja: egy már megnézett hónap visszalépésre a cache-ből jön, kérés nélkül. Az
  // adat havi granularitású előrejelzés, nem másodperces állapot — öt perc itt bőven pontos.
  const forecastQ = api.revenueForecast.useQuery(
    forecastKey,
    { query },
    { enabled, queryKey: forecastKey, staleTime: 5 * 60 * 1000 },
  );

  const body = forecastQ.data?.status === 200 ? forecastQ.data.body : undefined;

  // Részlet-modálok (szem-ikon) nyitott állapota.
  const [openModal, setOpenModal] = useState<'nrr' | 'prr' | 'pending' | 'inactive' | null>(null);

  // A modál-sorok normalizálása a közös DetailRow alakra (NRR: a scope a fiók-név jelenlétéből — legacy 1:1).
  const nrrRows: DetailRow[] = (body?.stats.nrrDetails ?? []).map((r) => ({
    service: r.service,
    planName: r.planName,
    scope: r.accountName ? 'account' : 'client',
    clientName: r.clientName,
    accountName: r.accountName,
    frequency: null,
    price: r.price,
  }));
  const prrRows: DetailRow[] = (body?.stats.prrDetails ?? []).map((r) => ({
    service: r.service,
    planName: r.planName,
    scope: r.scope,
    clientName: r.clientName,
    accountName: r.accountName,
    frequency: r.frequency,
    price: r.price,
  }));
  const pendingRows: DetailRow[] = (body?.stats.pendingDetails ?? []).map((r) => ({
    service: r.service,
    planName: r.planName,
    scope: r.scope,
    clientName: r.clientName,
    accountName: r.accountName,
    frequency: r.frequency,
    price: r.price,
  }));
  const inactiveRows: DetailRow[] = (
    body && 'inactiveDetails' in body.stats ? body.stats.inactiveDetails : []
  ).map((r) => ({
    service: r.service,
    planName: r.planName,
    scope: r.scope,
    clientName: r.clientName,
    accountName: r.accountName,
    frequency: r.frequency,
    price: r.price,
  }));
  const multiStatus = statuses.length > 1;

  // MRR dinamikus alfelirat: múlt / jelen / jövő (a legacy 1:1).
  const mrrSubtitleKey =
    month < currentMonth() ? 'historical' : month > currentMonth() ? 'projected' : 'current';

  const toolbar = (
    <ListToolbar
      // A HÓNAP mindig látható a sáv elején (mindhárom pénzügy-fülön egyformán): ez a lap fő dimenziója.
      leading={<MonthStepper month={month} onMonthChange={onMonthChange} />}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        // A belső lista-keresők EGYETLEN közös feliratot viselnek (CC2, `search-placeholder.wall.spec.ts`).
        placeholder: t('common.searchPlaceholder'),
      }}
      filterItems={filterItems}
      trailing={
        // a státusz-darabszám az ISMÉTLŐDŐ állományról szól — „eseti" nézetben rejtve
        body && !oneTimeOnly ? (
          <StatusCounts
            counts={{
              active: body.stats.activeCount,
              pending: body.stats.pendingCount,
              // egy régebbi BFF-válaszból hiányozhat (deploy-ablak) — akkor az elem nem jelenik meg
              inactive: 'inactiveCount' in body.stats ? body.stats.inactiveCount : undefined,
            }}
            selected={statuses}
            onSelect={selectStatus}
            // Függőben-modál: csak ha van függőben tétel ÉS nincs ügyfél/fiók-szűrő (a legacy 1:1).
            onShowInactive={() => setOpenModal('inactive')}
            onShowPending={
              !scoped && body.stats.pendingDetails.length > 0
                ? () => setOpenModal('pending')
                : undefined
            }
          />
        ) : undefined
      }
      meta={forecastQ.isFetching ? t('revenueForecast.loading') : ''}
    />
  );

  const isLoading = forecastQ.isLoading;
  const isError = !isLoading && (forecastQ.isError || (!!forecastQ.data && !body));
  // A KÉT hiba-eset SZÉTVÁLASZTVA: 401/403 → jog-hiba (az újrapróbálás értelmetlen), MINDEN más (502
  // átmeneti upstream-hiba, hálózat) → „Újra" gombos betöltési hiba. Élő lelet: a fojtott
  // upstream-olvasás 403-ként jött, a lap „nincs jogosultság"-ot írt arra a lapra, amire VAN jog — és a
  // felhasználónak nem volt mit tennie. A 502 EGY kattintásból újrapróbálható.
  // A nem-2xx válasz a ts-rest kliensen az `error`-ban jön (a `data` csak a 200-as ág) — ezért MINDKETTŐT
  // olvassuk, különben a jog-hiba is „próbáld újra"-ként jelenne meg.
  const status = forecastQ.data?.status ?? forecastQ.error?.status;
  const isForbidden = isForbiddenStatus(status);

  /**
   * ÜRES-ÁLLAPOT — a lap szintjén, nem kártyánként.
   *
   * MIÉRT KELL: a kimutatás aggregátum, nem lista; találat nélkül tehát nem „üres tábla" jelenik meg, hanem
   * EGY KÉPERNYŐNYI CSENDES NULLA. A „0 Ft" ugyanúgy néz ki, mint egy valódi nulla bevétel — a felhasználó
   * nem tudja megkülönböztetni a túl szűk szűrést az adat hiányától. A keresővel ez a helyzet mindennapos
   * lett, ezért mondjuk ki, melyik eset áll fenn.
   *
   * A JELZÉS: nincs a hónapban érvényes recurring tétel (`totalCount`) ÉS a bontás is üres (`byService` a
   * szűrt recurring + eseti halmazból épül) → tényleg semmi nem illeszkedett.
   */
  const isEmpty =
    !!body && body.stats.totalCount === 0 && body.charts.byService.length === 0 && !isLoading;

  /**
   * A LAP FIGYELMEZTETŐ SÁVJAI — a héj EGY blokkban, a fül-sáv ALATT rendereli őket (`TabView.notices`).
   *
   * SORREND = SÚLY: előbb az, ami a SZÁMOT teszi bizonytalanná (árfolyam-hiány, csonkolt olvasás, hiányzó
   * használati adat), utána a TEENDŐ (kihagyás-napló), végül a puszta tudnivaló (becsült árfolyam, árazott
   * használat). Korábban ugyanez a hat jelzés a tartalom KÜLÖNBÖZŐ pontjain ült — kettő a kártya-sorok közé
   * ékelődve, kettő puszta szövegsorként.
   */
  // a „Bevétel szolgáltatásonként" sora szűretlenül (szolgáltatás-szinten) a szolgáltatásra SZŰR
  const serviceClickable = !query.serviceId && !query.planId;
  // csomag-szinten (szűrt nézet) a sor a részletező csoportjához ugrik — ha a részletező a lapon van
  const planJumpable = !serviceClickable && (body?.charts.serviceDetails.length ?? 0) > 0;
  const notices: PageNotice[] = [];
  // AZ ADAT-HORIZONT — `'dataHorizon' in body`: a telepítési ablakban egy régebbi BFF-válaszból hiányozhat; ilyenkor
  // a blokk egyszerűen nem jelenik meg (a lap nem omlik össze).
  const horizon = body && 'dataHorizon' in body ? body.dataHorizon : null;
  // a kiválasztott hónap az ADAT-HORIZONT előtt van → nincs tény-szám (nem „még nem zárult le")
  const beforeActuals = !!body && horizon !== null && body.month < horizon.actualsFrom;
  if (body) {
    if (beforeActuals) {
      notices.push({
        key: 'before-horizon',
        tone: 'warning',
        title: t('revenueForecast.dataHorizon.beforeActuals', {
          month: formatMonthLabel(horizon?.actualsFrom ?? '', locale),
        }),
      });
    }
    // AZ ADAT-HORIZONT a vezetőségnek: visszamenőleg a kalkuláció adathiány miatt nem lehet pontos. A jelzés-sávban
    // áll (a nézet-kapcsoló és a dátumválasztó FÖLÖTT), mint az Összegzés többi jelzése.
    if (horizon !== null) {
      notices.push({
        key: 'data-horizon',
        tone: 'neutral',
        title: <DataHorizonNotice horizon={horizon} />,
      });
    }
    // a DEVIZA-SZABÁLY (terv és tény MÁS árfolyamon áll) a lenyitható jelzések közt — az eszköztár jobb szélén
    // mobilon nem is látszott
    notices.push({ key: 'currency', tone: 'neutral', title: t('revenueForecast.currencyNote') });
    // a KÉT SZABÁLY (havi: a szolgáltatás hónapja; periodikus: egyben, a számla hónapjában) — enélkül egy éves díj
    // egyetlen hónapban kiugrónak, a többiben hiányzónak látszana
    notices.push({ key: 'period-rule', tone: 'neutral', title: t('revenueForecast.periodRule') });
    // NEM-ALAP STÁTUSZ: kimondjuk, mire hat a szűrő és mire NEM — a rögzített viszonyítási kártyák (adott havi,
    // következő havi, a terv–tény trend tervvonala) az aktív állományt, a tény-számok a kiszámlázott bevételt
    // mutatják, státusztól függetlenül. Enélkül a szűrő-váltás után álló számok hibának látszanának.
    if (isNonDefaultStatus(statuses)) {
      notices.push({
        key: 'status-scope',
        tone: 'neutral',
        title: t('revenueForecast.statusScopeNote'),
      });
    }
    // Árfolyam-hiány a TERV-oldalon: a deviza-tételek 1:1-gyel szerepelnek → ALULBECSLÉS.
    if (body.usedFallbackRate) {
      notices.push({ key: 'rate', tone: 'warning', title: t('revenueForecast.fallbackRateNote') });
    }
    // Ugyanaz a TÉNY-oldalon — CSAK lezárt hónapon: nyitott hónapnak nincs tény-száma, amit minősíthetne.
    if (body.actual.closed && body.actual.rateSource === 'fallback') {
      notices.push({
        key: 'actual-rate',
        tone: 'warning',
        title: t('revenueForecast.actual.rateFallback'),
      });
    }
    if (body.actual.truncated) {
      notices.push({
        key: 'truncated',
        tone: 'warning',
        title: t('revenueForecast.actual.truncated'),
      });
    }
    // HASZNÁLAT-ALAPÚ tételek: magából az összegből NEM látszik, hogy sávos árat vagy csak alapdíjat mutat-e.
    // A FUTÓ / jövőbeli hónapra az adat még nem létezhet (a lezárás után jön) → semleges tájékoztatás, nem hiány
    // (user-lelet: a futó hónap „273 tétel nem kapott adatot" mondata hibát sugallt). LEZÁRT hónapban valódi
    // hiány → figyelmeztetés, lenyitható névsorral. `?? false` / `?? []`: a régebbi BFF-ből hiányozhat.
    if (body.usagePricing.missing > 0) {
      const missing = body.usagePricing.missing;
      const items = body.usagePricing.missingItems ?? [];
      notices.push(
        body.usagePricing.monthOpen === true
          ? {
              key: 'usage',
              tone: 'neutral',
              title: t('revenueForecast.usageOpenMonthNote', { count: missing }),
            }
          : {
              key: 'usage',
              tone: 'warning',
              title:
                items.length === 0 ? (
                  t('revenueForecast.usageMissingNote', { count: missing })
                ) : (
                  <UsageMissingList count={missing} items={items} />
                ),
            },
      );
    }
    // A TEENDŐ: a terv-tény eltérés önmagában rejtély; a kihagyás-napló nyitott tételei megmondják, hol
    // keresendő a hiány. A mélylink CSAK annak, akinek van joga a számla-modulhoz (a lap kapuja `finance.revenue.view`,
    // a naplóé `invoices.view`) — jog nélkül a MONDAT akkor is látszik, csak link nélkül.
    if (body.actual.closed && body.actual.skippedOpen > 0) {
      const label = t('revenueForecast.actual.skipped', { count: body.actual.skippedOpen });
      notices.push({
        key: 'skipped',
        tone: 'warning',
        title: can('invoices.view') ? (
          <Link
            to="/invoices"
            search={{ tab: 'skips', month: body.month }}
            className="underline underline-offset-2"
          >
            {label}
          </Link>
        ) : (
          label
        ),
      });
    }
    // TUDNIVALÓ (semleges): nincs teendő, csak a szám olvasatát pontosítja.
    if (body.actual.closed && body.actual.rateSource === 'estimated') {
      notices.push({
        key: 'estimated',
        tone: 'neutral',
        title: t('revenueForecast.actual.rateEstimated'),
      });
    }
    if (body.usagePricing.missing === 0 && body.usagePricing.priced > 0) {
      notices.push({
        key: 'priced',
        tone: 'neutral',
        title: t('revenueForecast.usagePricedNote', { count: body.usagePricing.priced }),
      });
    }
  }

  // A FÜL HOZZA A SAJÁT ELRENDEZÉSÉT (`TabView`): olvasó lap → `PageBody` térköz (a kiadás-fül a lista-lapok
  // `px-4 pb-2` dobozát kapja). A dialógusok portálon mennek, tehát a doboz nem érinti őket.
  const content = (
    // `tight`: a dátumválasztó-sor és az első blokk közé 12px kerül (user-kérés: kisebb sorköz) — ugyanaz a
    // ritmus, mint az Összegzés másik két al-nézetén.
    <PageBody align="left" tight>
      {isError ? (
        <ListErrorState forbidden={isForbidden} onRetry={() => void forecastQ.refetch()} />
      ) : isEmpty ? (
        <EmptyState
          title={t(hasNarrowing ? 'revenueForecast.emptyTitle' : 'revenueForecast.emptyMonthTitle')}
          description={t(
            hasNarrowing ? 'revenueForecast.emptyDesc' : 'revenueForecast.emptyMonthDesc',
          )}
        />
      ) : isLoading || !body ? (
        <div className="space-y-4">
          <Skeleton className="h-16 rounded-xl" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-xl" />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Kiemelt csíkok: az ADOTT (szűrt) havi (zöld) + a várható KÖVETKEZŐ havi bevétel (brand).
                A RÁCS SZÁNDÉKOSAN ASZIMMETRIKUS: a bal kártya HÁROM értéket hordoz (terv · tény · eltérés),
                a jobb egyet. Egyenlő hasábokon a bal kártya érték-blokkja a cím ALÁ tördelt, és a sor jobb
                fele üresen maradt (mérve 1920 px-en) — a 1.4/1 arány mindkettőt egy sorban tartja. */}
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            {/* Adott havi bevétel — a kiválasztott hónapra (zöld, a legacy 1:1). A kártya-váz a KÖZÖS
                  `HeroStatCard`: ugyanaz az alak, amit az Összegzés fül három összkép-kártyája is visel. */}
            <HeroStatCard
              tone="success"
              icon={<BanknotesIcon className="size-5" />}
              title={t('revenueForecast.selectedMonth.title')}
              description={t('revenueForecast.selectedMonth.desc')}
              valueSlot={
                <PlanVsActualValue
                  planLabel={t('revenueForecast.actual.planLabel')}
                  plan={fmt(body.stats.selectedMonth)}
                  actualLabel={t('revenueForecast.actual.actualLabel')}
                  actual={body.actual.closed ? fmt(body.actual.total) : null}
                  variancePct={body.actual.variancePct}
                  openLabel={
                    beforeActuals
                      ? t('revenueForecast.actual.noData')
                      : t('revenueForecast.actual.open')
                  }
                />
              }
            />

            {/* Várható következő havi bevétel — mindig a valódi következő naptári hónapra (brand). */}
            <HeroStatCard
              tone="brand"
              icon={<CalendarDaysIcon className="size-5" />}
              title={t('revenueForecast.nextMonth.title')}
              description={t('revenueForecast.nextMonth.desc')}
              value={fmt(body.stats.nextMonth)}
            />
          </div>

          {oneTimeOnly && (
            <Text variant="secondary" as="p" className="text-xs">
              {t('revenueForecast.oneTimeOnlyNote')}
            </Text>
          )}

          {/* 5 KPI-kártya (a legacy grid: 2 / 3 / 5 oszlop). */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-5">
            <StatCard
              title={t('revenueForecast.mrr.title')}
              value={fmt(body.stats.mrr)}
              // Az alfelirat CSAK akkor, ha az AKTUÁLIStól eltérő hónapot néz (múlt/jövő): a `current`
              // változat bájtra ugyanaz a mondat, mint a kártya `desc`-je, tehát duplán jelent meg.
              {...(mrrSubtitleKey === 'current'
                ? {}
                : { subtitle: t(`revenueForecast.mrr.subtitle.${mrrSubtitleKey}`) })}
              description={t('revenueForecast.mrr.desc')}
              info={t('revenueForecast.mrr.info')}
              badge={<ChangeBadge pct={body.stats.mrrChangePct} />}
              icon={ArrowTrendingUpIcon}
              footer={
                <StatusBreakdownBadges
                  breakdown={body.stats.mrrByStatus}
                  statuses={statuses}
                  fmt={fmt}
                  t={t}
                />
              }
            />
            <StatCard
              title={t('revenueForecast.prr.title')}
              value={fmt(body.stats.prr)}
              description={t('revenueForecast.prr.desc')}
              info={t('revenueForecast.prr.info')}
              icon={ArrowPathIcon}
              action={
                Number(body.stats.prr) > 0 ? (
                  <EyeButton
                    label={t('revenueForecast.details.prrTitle')}
                    onClick={() => setOpenModal('prr')}
                  />
                ) : undefined
              }
              footer={
                <>
                  {PERIODIC_FREQUENCIES.map((freq) => {
                    const row = body.stats.prrByFrequency.find((r) => r.frequency === freq);
                    if (!row || Number(row.amount) <= 0) return null;
                    // ugyanaz az ikon, mint a gyakoriság jelvényén mindenhol (`frequency` kategória)
                    const FreqIcon = badgeVisual('frequency', freq)?.icon;
                    return (
                      <Badge
                        key={freq}
                        variant="secondary"
                        className="tabular-nums"
                        icon={FreqIcon ? <FreqIcon /> : undefined}
                      >
                        {t(`revenueForecast.freqShort.${freq}`)} · {fmtShort(row.amount)}
                      </Badge>
                    );
                  })}
                  {multiStatus && (
                    <StatusBreakdownBadges
                      breakdown={body.stats.prrByStatus}
                      statuses={statuses}
                      fmt={fmt}
                      t={t}
                    />
                  )}
                </>
              }
            />
            <StatCard
              title={t('revenueForecast.nrr.title')}
              value={fmt(body.stats.nrr)}
              description={t('revenueForecast.nrr.desc')}
              info={t('revenueForecast.nrr.info')}
              icon={BoltIcon}
              action={
                Number(body.stats.nrr) > 0 ? (
                  <EyeButton
                    label={t('revenueForecast.details.nrrTitle')}
                    onClick={() => setOpenModal('nrr')}
                  />
                ) : undefined
              }
              footer={
                <StatusBreakdownBadges
                  breakdown={body.stats.nrrByStatus}
                  statuses={statuses}
                  fmt={fmt}
                  t={t}
                />
              }
            />
            <StatCard
              title={t('revenueForecast.arr.title')}
              value={fmt(body.stats.arr)}
              description={t('revenueForecast.arr.desc')}
              info={t('revenueForecast.arr.info')}
              icon={ChartBarIcon}
            />
            <StatCard
              title={t('revenueForecast.ar.title')}
              value={fmt(body.stats.ar)}
              description={t('revenueForecast.ar.desc')}
              info={t('revenueForecast.ar.info')}
              icon={BanknotesIcon}
            />
          </div>

          {/* Chartok. A top10-ügyfél és a főkategória-donut CSAK ügyfél/fiók-szűrő NÉLKÜL (a legacy 1:1).
                A RÁCS 1 / 2 / 3 hasáb: 1920 px-en két hasáb mellett a chartok fele üres terület volt, a
                sávok pedig indokolatlanul hosszúra nyúltak (user-lelet). A törés `xl`-en van, nem `2xl`-en:
                1280 px-en a harmadik hasáb már kiférne, de a kategória-tengely nevei ott csonkolnának. */}
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {/* A TERV–TÉNY trend a rács ELSŐ kártyája: ez a lap fő kérdése („tartjuk-e, amit a szerződések
                  ígérnek"). Az MRR-trend utána jön, mert az a FUTÓ ÜTEM külön kérdése. */}
            <ChartCard
              title={t('revenueForecast.charts.planVsActual')}
              emptyLabel={t('revenueForecast.charts.empty')}
              isEmpty={body.charts.planTrend.length === 0}
            >
              <PlanVsActualChart
                plan={body.charts.planTrend}
                actual={body.charts.actualTrend}
                fmt={fmt}
                fmtShort={fmtShort}
                monthLabel={(m) => formatMonthAxisLabel(m, locale)}
                planLabel={t('revenueForecast.charts.planSeries')}
                actualLabel={t('revenueForecast.charts.actualSeries')}
                ariaLabel={t('revenueForecast.charts.planVsActualAria')}
              />
            </ChartCard>

            {/* az MRR-trend az ISMÉTLŐDŐ bevételről szól — „eseti" nézetben rejtve */}
            {!oneTimeOnly && (
              <ChartCard
                title={t('revenueForecast.charts.mrrTrend')}
                emptyLabel={t('revenueForecast.charts.empty')}
                isEmpty={body.charts.mrrTrend.length === 0}
              >
                <TrendChart
                  data={body.charts.mrrTrend}
                  fmt={fmt}
                  fmtShort={fmtShort}
                  monthLabel={(m) => formatMonthAxisLabel(m, locale)}
                  ariaLabel={t('revenueForecast.charts.mrrTrendAria', {
                    value: fmt(body.charts.mrrTrend.at(-1)?.amount ?? '0'),
                  })}
                />
              </ChartCard>
            )}

            <ChartCard
              title={t('revenueForecast.charts.byService')}
              emptyLabel={t('revenueForecast.charts.empty')}
              isEmpty={body.charts.byService.length === 0}
              aside={
                serviceClickable || planJumpable ? (
                  <Text variant="secondary" as="span" className="text-xs">
                    {t(
                      serviceClickable
                        ? 'revenueForecast.charts.byServiceHint'
                        : 'revenueForecast.charts.byPlanHint',
                    )}
                  </Text>
                ) : undefined
              }
            >
              {/* SZOLGÁLTATÁSONKÉNT egy sor (szűretlenül), a sáv FIX / HASZNÁLAT-ALAPÚ részre osztva; egy sorra
                  kattintva a lap rászűr a szolgáltatásra, és a csomag-szintű bontás jelenik meg (user-döntés). */}
              <StackedBarList
                data={body.charts.byService.map((r) => ({
                  name: r.name,
                  recurring: r.fixed,
                  oneTime: r.usage,
                  total: r.amount,
                }))}
                fmt={fmt}
                recurringLabel={t('revenueForecast.charts.fixedFee')}
                oneTimeLabel={t('revenueForecast.charts.usageBased')}
                ariaLabel={t('revenueForecast.charts.byService')}
                onSelect={
                  serviceClickable
                    ? (name) => {
                        const id = body.charts.byService.find((r) => r.name === name)?.serviceId;
                        if (id) selectService(id);
                      }
                    : planJumpable
                      ? // CSOMAG-szinten a sor a lenti részletező csoportjához ugrik (user-kérés)
                        (name) => void scrollToServiceGroup(name)
                      : undefined
                }
              />
            </ChartCard>

            {!scoped && (
              <ChartCard
                title={t('revenueForecast.charts.byClient')}
                emptyLabel={t('revenueForecast.charts.empty')}
                isEmpty={body.charts.byClient.length === 0}
              >
                <StackedBarList
                  data={body.charts.byClient}
                  fmt={fmt}
                  recurringLabel={t('revenueForecast.charts.recurring')}
                  oneTimeLabel={t('revenueForecast.charts.oneTime')}
                  ariaLabel={t('revenueForecast.charts.byClient')}
                />
              </ChartCard>
            )}

            {!scoped && (
              <ChartCard
                title={t('revenueForecast.charts.categoryDistribution')}
                emptyLabel={t('revenueForecast.charts.empty')}
                isEmpty={body.charts.categoryDistribution.every((c) => Number(c.amount) <= 0)}
              >
                <DonutChart
                  data={body.charts.categoryDistribution}
                  fmt={fmt}
                  fmtShort={fmtShort}
                  categoryLabel={(cat) => t(`revenueForecast.charts.legendCategory.${cat}`)}
                  ariaLabel={t('revenueForecast.charts.categoryAria')}
                />
              </ChartCard>
            )}
          </div>

          {/* A TÉNY-oldal bontásai — CSAK lezárt hónapra (nyitott hónapon nincs mit bontani). Ugyanaz a
                három vágás, mint a terv-oldalon, hogy a kettő ÖSSZEVETHETŐ legyen. A `null` nevű sor a kézi
                számla / már törölt hozzárendelés: a címkéjét a felület adja, a BFF nem hardcode-ol szöveget. */}
          {body.actual.closed && (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              <ChartCard
                title={t('revenueForecast.charts.actualByService')}
                emptyLabel={t('revenueForecast.charts.empty')}
                isEmpty={body.actual.byService.length === 0}
              >
                <BarList
                  data={body.actual.byService.map((r) => ({
                    name: r.name ?? t('revenueForecast.actual.unclassified'),
                    amount: r.amount,
                  }))}
                  fmt={fmt}
                  ariaLabel={t('revenueForecast.charts.actualByService')}
                />
              </ChartCard>

              {!scoped && (
                <ChartCard
                  title={t('revenueForecast.charts.actualByClient')}
                  emptyLabel={t('revenueForecast.charts.empty')}
                  isEmpty={body.actual.byClient.length === 0}
                >
                  <BarList
                    data={body.actual.byClient.map((r) => ({
                      name: r.name ?? t('revenueForecast.actual.unknownClient'),
                      amount: r.amount,
                    }))}
                    fmt={fmt}
                    ariaLabel={t('revenueForecast.charts.actualByClient')}
                  />
                </ChartCard>
              )}

              {!scoped && (
                <ChartCard
                  title={t('revenueForecast.charts.actualCategory')}
                  emptyLabel={t('revenueForecast.charts.empty')}
                  isEmpty={body.actual.categoryDistribution.every((c) => Number(c.amount) <= 0)}
                >
                  <DonutChart
                    data={body.actual.categoryDistribution}
                    fmt={fmt}
                    fmtShort={fmtShort}
                    categoryLabel={(cat) => t(`revenueForecast.charts.legendCategory.${cat}`)}
                    ariaLabel={t('revenueForecast.charts.actualCategory')}
                  />
                </ChartCard>
              )}
            </div>
          )}

          {/* Szolgáltatás-részletes drilldown — a BFF CSAK ügyfél/fiók/szolgáltatás/csomag/alkategória-szűrőnél
                tölti (a byService-oszlopok sorrendjében); egyébként üres → nem renderel. */}
          {body.charts.serviceDetails.length > 0 && (
            // a látható (szűrt) tételek exportja — a kolléga Excelben könnyebben átlátja a listát
            <div className="flex justify-end">
              <ServiceDetailsExport
                groups={body.charts.serviceDetails}
                month={month}
                auditFilters={{
                  month,
                  q: query.q,
                  status: query.status?.join(','),
                  billingType: query.billingType,
                  serviceId: query.serviceId,
                  planId: query.planId,
                  clientId: query.clientId,
                  accountId: query.accountId,
                  category: query.subCategory?.join(','),
                }}
                t={t}
              />
            </div>
          )}
          <ServiceDrilldown groups={body.charts.serviceDetails} fmt={fmt} t={t} locale={locale} />
        </div>
      )}

      {/* Szem-ikon részlet-modálok (NRR / PRR / függőben tételek). */}
      <RevenueDetailDialog
        open={openModal === 'nrr'}
        onClose={() => setOpenModal(null)}
        title={t('revenueForecast.details.nrrTitle')}
        rows={nrrRows}
        fmt={fmt}
        t={t}
      />
      <RevenueDetailDialog
        open={openModal === 'prr'}
        onClose={() => setOpenModal(null)}
        title={t('revenueForecast.details.prrTitle')}
        rows={prrRows}
        fmt={fmt}
        t={t}
      />
      <RevenueDetailDialog
        open={openModal === 'inactive'}
        onClose={() => setOpenModal(null)}
        title={t('revenueForecast.details.inactiveTitle')}
        description={t('revenueForecast.details.inactiveDesc')}
        rows={inactiveRows}
        fmt={fmt}
        t={t}
      />
      <RevenueDetailDialog
        open={openModal === 'pending'}
        onClose={() => setOpenModal(null)}
        title={t('revenueForecast.details.pendingTitle')}
        rows={pendingRows}
        fmt={fmt}
        t={t}
      />
    </PageBody>
  );

  return { toolbar, notices, content, hasNarrowing };
}

/**
 * A LEZÁRT hónap hiányzó használati adatai — a mondat + lenyitható névsor (ügyfél · fiók · tétel). A natív
 * `<details>` billentyűzettel és képernyőolvasóval is kezelhető, és nem kell hozzá állapot.
 */
function UsageMissingList({
  count,
  items,
}: {
  count: number;
  items: readonly {
    clientName: string | null;
    accountName: string | null;
    itemName: string | null;
  }[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <details className="group">
      <summary className="cursor-pointer list-none">
        {t('revenueForecast.usageMissingNote', { count })}{' '}
        <span className="underline underline-offset-2">
          {t('revenueForecast.usageMissingShow')}
        </span>
      </summary>
      <ul className="mt-2 list-disc space-y-0.5 pl-5">
        {items.map((it, i) => (
          <li key={i}>
            {[it.clientName, it.accountName, it.itemName]
              .filter((v) => v !== null && v !== '')
              .join(' · ')}
          </li>
        ))}
      </ul>
      {count > items.length ? (
        <p className="mt-1">
          {t('revenueForecast.usageMissingMore', { count: count - items.length })}
        </p>
      ) : null}
    </details>
  );
}
